import React, { useEffect, useState, useCallback } from 'react';
import { FieldInput } from '../components/ui/FieldInput';
import { Divider } from '../components/ui/Divider';
import { Tabs } from '../components/ui/Tabs';
import { JoySelectField } from '../components/ui/JoySelectField';
import { AppPagination } from '../components/ui/AppPagination';
import DateTimePicker from '../components/ui/DateTimePicker';
import { copyToClipboard } from '../utils/clipboard';
import { formatDateTimeMsk } from '../utils/date';
import { useToast } from '../contexts/ToastContext';

import CircularProgress from '@mui/material/CircularProgress';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";
const SETTINGS_URL = `${API_BASE_URL}/api/v1/settings/`;

const SETTING_LABELS: Record<string, string> = {
    "SDO_API_URL": "URL API СДО",
    "SDO_USERNAME": "Имя пользователя СДО",
    "SDO_PASSWORD": "Пароль СДО",
    "SMTP_HOST": "Хост SMTP",
    "SMTP_PORT": "Порт SMTP",
    "SMTP_USERNAME": "Имя пользователя SMTP",
    "SMTP_PASSWORD": "Пароль SMTP",
    "SMTP_FROM_EMAIL": "Email отправителя (SMTP)",
    "REPORT_BASE_PATH": "Путь для сохранения отчетов"
};

interface SettingsPageProps {
    onLogout: () => void;
    username: string;
    role: string;
}

interface ReportLogItem {
    id: number;
    sdo_attempt_id: string;
    student_login: string | null;
    student_full_name: string | null;
    course_name: string | null;
    status: string;
    error_stage: string | null;
    error_message: string | null;
    report_path: string | null;
    triggered_by: string;
    created_at: string;
}

interface StorageHealth {
    path: string;
    mounted: boolean;
    writable: boolean;
    error: string | null;
}

interface CeleryHealth {
    worker: {
        online: boolean;
        last_task_at: string | null;
        success_count: number;
        failure_count: number;
    };
    beat: {
        online: boolean;
        last_tick_at: string | null;
        dispatch_count: number;
    };
}

interface EmailNotificationTypeSettings {
    enabled: boolean;
    redirect_only: boolean;
    redirect_address: string;
}

interface EmailNotificationSettings {
    creation: EmailNotificationTypeSettings;
    risk: EmailNotificationTypeSettings;
    completion: EmailNotificationTypeSettings;
}

const DEFAULT_NOTIFICATION_TYPE: EmailNotificationTypeSettings = {
    enabled: false,
    redirect_only: true,
    redirect_address: 'prog2@sdoenergy.ru',
};

const DEFAULT_NOTIFICATION_SETTINGS: EmailNotificationSettings = {
    creation: { ...DEFAULT_NOTIFICATION_TYPE },
    risk: { ...DEFAULT_NOTIFICATION_TYPE },
    completion: { ...DEFAULT_NOTIFICATION_TYPE },
};

const SettingsPage: React.FC<SettingsPageProps> = ({ onLogout }) => {
    const { addToast } = useToast();

    // Общие состояния
    const [activeTab, setActiveTab] = useState<string>("general");
    const [settings, setSettings] = useState<Record<string, string>>({});
    const [loadingSettings, setLoadingSettings] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // Состояния репортинга
    const [reportingActive, setReportingActive] = useState<boolean | null>(null);
    const [loadingReportStatus, setLoadingReportStatus] = useState(false);
    const [storageHealth, setStorageHealth] = useState<StorageHealth | null>(null);
    const [celeryHealth, setCeleryHealth] = useState<CeleryHealth | null>(null);
    const [healthLoading, setHealthLoading] = useState(false);
    const [notificationSettings, setNotificationSettings] = useState<EmailNotificationSettings>(
        DEFAULT_NOTIFICATION_SETTINGS
    );
    const [notificationSettingsLoading, setNotificationSettingsLoading] = useState(false);
    const [notificationSettingsSaving, setNotificationSettingsSaving] = useState(false);

    // Логи загрузок
    const [logs, setLogs] = useState<ReportLogItem[]>([]);
    const [logsTotal, setLogsTotal] = useState(0);
    const [logsPage, setLogsPage] = useState(1);
    const [logsSize] = useState(15);
    const [logsSearch, setLogsSearch] = useState("");
    const [logsStatusFilter, setLogsStatusFilter] = useState("ALL");
    const [loadingLogs, setLoadingLogs] = useState(false);

    // Временной интервал для принудительной синхронизации
    const [forceDateFrom, setForceDateFrom] = useState<string | null>(null);
    const [forceDateTo, setForceDateTo] = useState<string | null>(null);
    const [manualActionLoading, setManualActionLoading] = useState(false);

    const getHeaders = useCallback(() => {
        const token = localStorage.getItem('token');
        return {
            'Accept': 'application/json',
            'Authorization': token || '',
            'Content-Type': 'application/json'
        };
    }, []);

    const fetchSettings = useCallback(async () => {
        setLoadingSettings(true);
        try {
            const res = await fetch(SETTINGS_URL, { headers: getHeaders() });
            if (res.status === 401) { onLogout(); return; }
            if (!res.ok) throw new Error("Не удалось загрузить настройки");
            const data = await res.json();
            setSettings(data);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoadingSettings(false);
        }
    }, [getHeaders, onLogout]);

    const fetchReportingStatus = useCallback(async () => {
        setLoadingReportStatus(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/v1/reporting/status`, { headers: getHeaders() });
            if (res.status === 401) { onLogout(); return; }
            if (res.ok) {
                const data = await res.json();
                setReportingActive(data.status === "enabled");
            }
        } catch (err) {
            console.error("Ошибка при получении статуса репортинга", err);
        } finally {
            setLoadingReportStatus(false);
        }
    }, [getHeaders, onLogout]);

    const fetchHealthStatus = useCallback(async () => {
        setHealthLoading(true);
        try {
            const [storageRes, celeryRes] = await Promise.all([
                fetch(`${API_BASE_URL}/api/v1/reporting/storage-health`, { headers: getHeaders() }),
                fetch(`${API_BASE_URL}/api/v1/reporting/celery-health`, { headers: getHeaders() })
            ]);

            if (storageRes.ok) setStorageHealth(await storageRes.json());
            if (celeryRes.ok) setCeleryHealth(await celeryRes.json());
        } catch (err) {
            console.error("Ошибка диагностики системы", err);
        } finally {
            setHealthLoading(false);
        }
    }, [getHeaders]);

    const fetchNotificationSettings = useCallback(async () => {
        setNotificationSettingsLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/v1/reporting/notification-settings`, {
                headers: getHeaders(),
            });
            if (res.status === 401) { onLogout(); return; }
            if (res.ok) {
                const data = await res.json();
                setNotificationSettings(data);
            }
        } catch (err) {
            console.error("Ошибка при загрузке настроек уведомлений", err);
        } finally {
            setNotificationSettingsLoading(false);
        }
    }, [getHeaders, onLogout]);

    const fetchLogs = useCallback(async (pageNumber: number, filter: string, searchVal: string) => {
        setLoadingLogs(true);
        try {
            const params = new URLSearchParams({
                page: pageNumber.toString(),
                size: logsSize.toString()
            });
            if (searchVal.trim()) params.append('search', searchVal.trim());
            if (filter !== 'ALL') params.append('status_filter', filter.toLowerCase());

            const res = await fetch(`${API_BASE_URL}/api/v1/reporting/download-logs?${params.toString()}`, {
                headers: getHeaders()
            });

            if (res.ok) {
                const data = await res.json();
                setLogs(data.items || []);
                setLogsTotal(data.total || 0);
            }
        } catch (err) {
            console.error("Ошибка при загрузке логов", err);
        } finally {
            setLoadingLogs(false);
        }
    }, [logsSize, getHeaders]);

    useEffect(() => {
        fetchSettings();
        fetchReportingStatus();
        fetchHealthStatus();
        fetchNotificationSettings();
    }, [fetchSettings, fetchReportingStatus, fetchHealthStatus, fetchNotificationSettings]);

    useEffect(() => {
        if (activeTab === "logs") {
            fetchLogs(logsPage, logsStatusFilter, logsSearch);
        }
    }, [activeTab, logsPage, logsStatusFilter, logsSearch, fetchLogs]);

    const handleInputChange = (key: string, value: string) => {
        setSettings(prev => ({ ...prev, [key]: value }));
    };

    const handleSaveSettings = async () => {
        setIsSaving(true);
        setError(null);
        setSuccess(null);
        try {
            const res = await fetch(SETTINGS_URL, {
                method: 'PUT',
                headers: getHeaders(),
                body: JSON.stringify(settings),
            });

            if (res.status === 401) { onLogout(); return; }
            if (!res.ok) {
                let msg = `Ошибка сохранения (${res.status})`;
                try {
                    const errBody = await res.json();
                    msg = errBody?.detail || msg;
                } catch {}
                throw new Error(msg);
            }
            const data = await res.json();
            setSettings(data);
            setSuccess("Настройки успешно сохранены!");
        } catch (err: any) {
            setError(err.message);
        } finally {
            setIsSaving(false);
            setTimeout(() => { setSuccess(null); setError(null); }, 4000);
        }
    };

    const saveNotificationSettings = async (next: EmailNotificationSettings) => {
        const previous = notificationSettings;
        setNotificationSettings(next);
        setNotificationSettingsSaving(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/v1/reporting/notification-settings`, {
                method: 'PUT',
                headers: getHeaders(),
                body: JSON.stringify(next),
            });
            if (res.status === 401) { onLogout(); return; }
            if (!res.ok) throw new Error("Не удалось сохранить настройки уведомлений");
            const data = await res.json();
            setNotificationSettings(data);
            setSuccess("Настройки уведомлений сохранены");
            setTimeout(() => setSuccess(null), 3000);
        } catch (err: any) {
            setNotificationSettings(previous);
            setError(err.message);
            setTimeout(() => setError(null), 4000);
        } finally {
            setNotificationSettingsSaving(false);
        }
    };

    const handleNotificationTypeChange = (
        typeKey: keyof EmailNotificationSettings,
        patch: Partial<EmailNotificationTypeSettings>
    ) => {
        const next: EmailNotificationSettings = {
            ...notificationSettings,
            [typeKey]: { ...notificationSettings[typeKey], ...patch },
        };
        saveNotificationSettings(next);
    };

    const handleToggleReporting = async (start: boolean) => {
        setLoadingReportStatus(true);
        try {
            const endpoint = start ? 'start' : 'stop';
            const res = await fetch(`${API_BASE_URL}/api/v1/reporting/${endpoint}`, {
                method: 'POST',
                headers: getHeaders()
            });
            if (res.ok) {
                setReportingActive(start);
                setSuccess(`Планировщик успешно ${start ? "запущен" : "остановлен"}`);
                setTimeout(() => setSuccess(null), 3000);
            }
        } catch (err) {
            setError("Не удалось изменить состояние планировщика");
        } finally {
            setLoadingReportStatus(false);
        }
    };

    const handleRunNow = async () => {
        setManualActionLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/v1/reporting/run-now`, {
                method: 'POST',
                headers: getHeaders()
            });
            if (res.ok) {
                setSuccess("Синхронизация успешно запущена в фоновом режиме");
                setTimeout(() => setSuccess(null), 4000);
            }
        } catch (err) {
            setError("Не удалось запустить сбор отчетов");
        } finally {
            setManualActionLoading(false);
        }
    };

    const handleForceCheck = async () => {
        if (!forceDateFrom) {
            alert("Пожалуйста, выберите дату начала диапазона.");
            return;
        }
        setManualActionLoading(true);
        try {
            const payload = {
                date_from: forceDateFrom,
                date_to: forceDateTo || null
            };

            const res = await fetch(`${API_BASE_URL}/api/v1/reporting/force-check`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify(payload)
            });

            if (res.ok) {
                setSuccess("Принудительная проверка запущена. Процесс займет некоторое время");
                setForceDateFrom(null);
                setForceDateTo(null);
                setTimeout(() => setSuccess(null), 4000);
            } else {
                const err = await res.json().catch(() => ({}));
                alert(`Ошибка принудительной загрузки: ${err.detail || 'Неизвестная ошибка'}`);
            }
        } catch (err) {
            setError("Не удалось запустить принудительную синхронизацию");
        } finally {
            setManualActionLoading(false);
        }
    };

    // Функция безопасного копирования в буфер с Toast-уведомлением
    const handleCopy = async (text: string, successMessage: string) => {
        const result = await copyToClipboard(text);
        if (result && addToast) {
            try {
                (addToast as any)({ message: successMessage, type: 'success' });
            } catch {
                try {
                    (addToast as any)(successMessage, 'success');
                } catch (e) {
                    console.error("Ошибка вызова Toast:", e);
                }
            }
        }
    };

    const renderStatusAsText = (status: string) => {
        const normalized = (status || "").toLowerCase();
        switch (normalized) {
            case 'success':
                return <span className="font-semibold text-green-600">Успешно</span>;
            case 'failed':
                return <span className="font-semibold text-red-600">Ошибка</span>;
            case 'skipped':
                return <span className="font-semibold text-gray-500">Пропущено</span>;
            default:
                return <span className="font-semibold text-gray-400">{status}</span>;
        }
    };

    const tabItems = [
        {
            id: "general",
            label: "Основные настройки",
            content: (
                <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                    <div className="p-8 space-y-8">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div className="space-y-5">
                                <h3 className="text-sm font-semibold text-gray-900 tracking-wide border-b border-gray-100 pb-2">
                                    Интеграция с СДО
                                </h3>
                                {["SDO_API_URL", "SDO_USERNAME", "SDO_PASSWORD"].map(key => (
                                    <FieldInput
                                        key={key}
                                        label={SETTING_LABELS[key] || key}
                                        value={settings[key] || ''}
                                        onChange={(val: string) => handleInputChange(key, val)}
                                        type={key.includes("PASSWORD") ? "password" : "text"}
                                    />
                                ))}
                            </div>

                            <div className="space-y-5">
                                <h3 className="text-sm font-semibold text-gray-900 tracking-wide border-b border-gray-100 pb-2">
                                    Почтовый сервер (SMTP)
                                </h3>
                                {["SMTP_HOST", "SMTP_PORT", "SMTP_USERNAME", "SMTP_PASSWORD", "SMTP_FROM_EMAIL"].map(key => (
                                    <FieldInput
                                        key={key}
                                        label={SETTING_LABELS[key] || key}
                                        value={settings[key] || ''}
                                        onChange={(val: string) => handleInputChange(key, val)}
                                        type={key.includes("PASSWORD") ? "password" : "text"}
                                    />
                                ))}
                            </div>
                        </div>

                        <div className="space-y-5 border-t border-gray-100 pt-6">
                            <h3 className="text-sm font-semibold text-gray-900 tracking-wide">
                                Пути сохранения
                            </h3>
                            <FieldInput
                                label={SETTING_LABELS["REPORT_BASE_PATH"]}
                                value={settings["REPORT_BASE_PATH"] || ''}
                                onChange={(val: string) => handleInputChange("REPORT_BASE_PATH", val)}
                            />
                        </div>
                    </div>

                    <div className="px-8 py-4 bg-gray-50 border-t border-gray-200 flex justify-end items-center gap-4">
                        {error && <span className="text-sm text-red-600 font-medium">{error}</span>}
                        {success && <span className="text-sm text-green-600 font-medium">{success}</span>}
                        <button
                            type="button"
                            onClick={handleSaveSettings}
                            disabled={isSaving}
                            className="inline-flex items-center justify-center gap-2 rounded-md px-6 py-2 text-sm font-semibold text-white shadow-sm transition-colors bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400"
                        >
                            {isSaving && <CircularProgress size={16} color="inherit" />}
                            {isSaving ? 'Сохранение...' : 'Сохранить изменения'}
                        </button>
                    </div>
                </div>
            )
        },
        {
            id: "reporting",
            label: "Мониторинг СДО",
            content: (
                <div className="space-y-6">
                    {/* 1. Управление автоматической загрузкой */}
                    <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                        <h3 className="text-sm font-semibold text-gray-900 mb-4 border-b border-gray-100 pb-2">Управление автоматической загрузкой</h3>
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                            <div className="space-y-1">
                                <p className="text-sm text-gray-700">
                                    Состояние планировщика периодических отчетов:{" "}
                                    {loadingReportStatus ? (
                                        <span className="text-gray-400">Проверка...</span>
                                    ) : reportingActive ? (
                                        <span className="text-green-600 font-semibold">Включен</span>
                                    ) : (
                                        <span className="text-red-500 font-semibold">Приостановлен</span>
                                    )}
                                </p>
                                <p className="text-xs text-gray-500">
                                    Планировщик автоматически опрашивает СДО на наличие новых завершенных попыток сдачи экзаменов и тестов.
                                </p>
                            </div>
                            <div className="flex flex-wrap items-center gap-3">
                                {reportingActive ? (
                                    <button
                                        type="button"
                                        onClick={() => handleToggleReporting(false)}
                                        disabled={loadingReportStatus}
                                        className="rounded-md border border-red-200 bg-white px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-50"
                                    >
                                        Остановить планировщик
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => handleToggleReporting(true)}
                                        disabled={loadingReportStatus}
                                        className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                                    >
                                        Включить планировщик
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={handleRunNow}
                                    disabled={manualActionLoading}
                                    className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                                >
                                    {manualActionLoading ? "Выполняется..." : "Запустить сбор сейчас"}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* 2. Принудительное скачивание за период */}
                    <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                        <h3 className="text-sm font-semibold text-gray-900 mb-4 border-b border-gray-100 pb-2">Принудительное скачивание за период</h3>
                        <p className="text-xs text-gray-500 mb-5">
                            Позволяет запустить глубокое скачивание результатов СДО в обход автоматического фильтра планировщика. Ранее успешно сохраненные отчеты не будут затронуты.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
                            <DateTimePicker
                                label="Дата начала (с)"
                                value={forceDateFrom}
                                onChange={setForceDateFrom}
                            />
                            <DateTimePicker
                                label="Дата окончания (по)"
                                value={forceDateTo}
                                onChange={setForceDateTo}
                            />
                            <button
                                type="button"
                                onClick={handleForceCheck}
                                disabled={manualActionLoading || !forceDateFrom}
                                className="w-full rounded-md bg-blue-600 px-5 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:bg-gray-300 disabled:opacity-50 transition-colors shadow-sm h-[40px] flex items-center justify-center"
                            >
                                {manualActionLoading ? "Выполняется запуск..." : "Запустить синхронизацию"}
                            </button>
                        </div>
                    </div>

                    {/* 3. Мониторинг хранилища и Celery */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Статус хранилища отчетов */}
                        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                            <h3 className="text-sm font-semibold text-gray-900 mb-4 border-b border-gray-100 pb-2">Хранилище результатов</h3>
                            {healthLoading ? (
                                <div className="flex justify-center py-6"><CircularProgress size={24} /></div>
                            ) : storageHealth ? (
                                <div className="space-y-3 text-sm">
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-500">Путь монтирования:</span>
                                        <div className="flex items-center gap-1.5 overflow-hidden">
                                            <span className="font-mono text-xs max-w-[180px] truncate text-gray-800" title={storageHealth.path}>
                                                {storageHealth.path}
                                            </span>
                                            {/* Кнопка копирования пути монтирования диска */}
                                            <button
                                                onClick={() => handleCopy(storageHealth.path, 'Путь монтирования скопирован')}
                                                className="text-gray-400 hover:text-blue-600 transition-colors shrink-0"
                                                title="Копировать путь"
                                            >
                                                <ContentCopyIcon style={{ fontSize: 13 }} />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-500">Диск смонтирован:</span>
                                        <span className={storageHealth.mounted ? "text-green-600 font-semibold" : "text-red-500 font-semibold"}>
                                            {storageHealth.mounted ? "Да" : "Нет"}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-500">Права на запись:</span>
                                        <span className={storageHealth.writable ? "text-green-600 font-semibold" : "text-red-500 font-semibold"}>
                                            {storageHealth.writable ? "Доступно" : "Только чтение / Ошибка"}
                                        </span>
                                    </div>
                                    {storageHealth.error && (
                                        <div className="bg-red-50 border border-red-100 rounded p-2 text-xs text-red-600 mt-2">
                                            {storageHealth.error}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <p className="text-sm text-gray-400">Информация о диске недоступна</p>
                            )}
                        </div>

                        {/* Статус Celery фоновых воркеров */}
                        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                            <h3 className="text-sm font-semibold text-gray-900 mb-4 border-b border-gray-100 pb-2">Фоновые обработчики (Celery)</h3>
                            {healthLoading ? (
                                <div className="flex justify-center py-6"><CircularProgress size={24} /></div>
                            ) : celeryHealth ? (
                                <div className="space-y-4 text-sm">
                                    <div>
                                        <div className="flex justify-between items-center mb-1">
                                            <span className="text-gray-700 font-semibold">Воркер задач (Celery Worker):</span>
                                            <span className={`px-2 py-0.5 rounded text-xs ${celeryHealth.worker.online ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                                                {celeryHealth.worker.online ? "В сети" : "Недоступен"}
                                            </span>
                                        </div>
                                        {celeryHealth.worker.online && (
                                            <div className="flex flex-col gap-1 text-xs text-gray-500 pl-2 mt-2">
                                                <div>Успешных задач: <span className="font-semibold text-gray-800">{celeryHealth.worker.success_count}</span></div>
                                                <div>Ошибок в задачах: <span className="font-semibold text-red-600">{celeryHealth.worker.failure_count}</span></div>
                                                <div className="text-gray-400 mt-1">Последний запуск: {celeryHealth.worker.last_task_at ? formatDateTimeMsk(celeryHealth.worker.last_task_at) : '—'}</div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="border-t border-gray-100 pt-3">
                                        <div className="flex justify-between items-center mb-1">
                                            <span className="text-gray-700 font-semibold">Планировщик (Celery Beat):</span>
                                            <span className={`px-2 py-0.5 rounded text-xs ${celeryHealth.beat.online ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                                                {celeryHealth.beat.online ? "В сети" : "Недоступен"}
                                            </span>
                                        </div>
                                        {celeryHealth.beat.online && (
                                            <div className="flex flex-col gap-1 text-xs text-gray-500 pl-2 mt-2">
                                                <div>Задач отправлено: <span className="font-semibold text-gray-800">{celeryHealth.beat.dispatch_count}</span></div>
                                                <div className="text-gray-400 mt-1">Последний тик: {celeryHealth.beat.last_tick_at ? formatDateTimeMsk(celeryHealth.beat.last_tick_at) : '—'}</div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <p className="text-sm text-gray-400">Информация об очередях недоступна</p>
                            )}
                        </div>
                    </div>

                    {/* 4. Управление email-уведомлениями */}
                    <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                        <h3 className="text-sm font-semibold text-gray-900 mb-1 border-b border-gray-100 pb-2">
                            Email-уведомления
                        </h3>
                        <p className="text-xs text-gray-500 mb-5">
                            Управление типами автоматических и ручных рассылок. Для боевого тестирования
                            включите нужный тип и отметьте «только на указанный адрес» — письма не уйдут
                            студентам, ответственным и менеджерам отделов.
                        </p>
                        {notificationSettingsLoading ? (
                            <div className="flex justify-center py-6"><CircularProgress size={24} /></div>
                        ) : (
                            <div className="space-y-6">
                                {([
                                    {
                                        typeKey: 'creation' as const,
                                        title: 'Учётные данные при создании заявки',
                                        description: 'Письма студентам и ответственным, отправляемые вручную кнопкой «Отправить» в карточке заявки.',
                                    },
                                    {
                                        typeKey: 'risk' as const,
                                        title: 'Уведомления о риске заказчикам',
                                        description: 'Автоматические письма ответственному специалисту при статусах «Подходит к концу» и «Просрочено».',
                                    },
                                    {
                                        typeKey: 'completion' as const,
                                        title: 'Завершение обучения менеджерам организации',
                                        description: 'Автоматические письма менеджерам отделов при закрытии заявки (статус CLOSED).',
                                    },
                                ]).map((item) => {
                                    const cfg = notificationSettings[item.typeKey];
                                    return (
                                    <div
                                        key={item.typeKey}
                                        className="py-4 border-b border-gray-100 last:border-b-0 space-y-3"
                                    >
                                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                            <div className="flex-1 pr-4">
                                                <p className="text-sm font-medium text-gray-900">{item.title}</p>
                                                <p className="text-xs text-gray-500 mt-0.5">{item.description}</p>
                                            </div>
                                            <div className="flex items-center gap-2 shrink-0">
                                                <span className="text-xs text-gray-500">Включено</span>
                                                <button
                                                    type="button"
                                                    role="switch"
                                                    aria-checked={cfg.enabled}
                                                    disabled={notificationSettingsSaving}
                                                    onClick={() => handleNotificationTypeChange(
                                                        item.typeKey,
                                                        { enabled: !cfg.enabled }
                                                    )}
                                                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 ${
                                                        cfg.enabled ? 'bg-blue-600' : 'bg-gray-200'
                                                    }`}
                                                >
                                                    <span
                                                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                                            cfg.enabled ? 'translate-x-5' : 'translate-x-0'
                                                        }`}
                                                    />
                                                </button>
                                            </div>
                                        </div>
                                        <div className="flex flex-col sm:flex-row sm:items-center gap-3 pl-0 sm:pl-1">
                                            <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={cfg.redirect_only}
                                                    disabled={notificationSettingsSaving}
                                                    onChange={(e) => handleNotificationTypeChange(
                                                        item.typeKey,
                                                        { redirect_only: e.target.checked }
                                                    )}
                                                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                />
                                                Отправлять только на указанный адрес
                                            </label>
                                            <input
                                                type="email"
                                                value={cfg.redirect_address}
                                                disabled={notificationSettingsSaving || !cfg.redirect_only}
                                                onChange={(e) => setNotificationSettings({
                                                    ...notificationSettings,
                                                    [item.typeKey]: {
                                                        ...cfg,
                                                        redirect_address: e.target.value,
                                                    },
                                                })}
                                                onBlur={(e) => handleNotificationTypeChange(
                                                    item.typeKey,
                                                    { redirect_address: e.target.value.trim() }
                                                )}
                                                placeholder="test@example.com"
                                                className="flex-1 max-w-sm h-9 rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                                            />
                                        </div>
                                    </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            )
        },
        {
            id: "logs",
            label: "Логи импорта отчетов",
            content: (
                <div className="space-y-6">
                    <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm grid grid-cols-1 md:grid-cols-2 gap-6 items-end">
                        <div className="space-y-1">
                            <label className="block text-sm font-medium text-gray-700">Поиск по ключевым словам</label>
                            <input
                                type="text"
                                placeholder="ФИО, логин, название курса, UUID попытки..."
                                value={logsSearch}
                                onChange={(e) => { setLogsSearch(e.target.value); setLogsPage(1); }}
                                className="w-full h-[42px] rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm placeholder:text-gray-400"
                            />
                        </div>
                        <div>
                            <JoySelectField
                                label="Фильтр по статусу"
                                value={logsStatusFilter}
                                onChange={(val) => { setLogsStatusFilter(val || "ALL"); setLogsPage(1); }}
                                options={[
                                    { value: "ALL", label: "Все" },
                                    { value: "success", label: "Успешные" },
                                    { value: "failed", label: "С ошибкой" },
                                    { value: "skipped", label: "Пропущенные" },
                                ]}
                            />
                        </div>
                    </div>

                    {/* Таблица логов */}
                    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                        {loadingLogs ? (
                            <div className="flex justify-center items-center py-20"><CircularProgress /></div>
                        ) : logs.length === 0 ? (
                            <div className="text-center py-16 text-gray-500">Записей в логе не найдено</div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wider">
                                        <tr>
                                            <th className="px-6 py-4 text-left font-semibold">Дата запуска</th>
                                            <th className="px-6 py-4 text-left font-semibold">Слушатель / ID попытки</th>
                                            <th className="px-6 py-4 text-left font-semibold">Курс / Программа</th>
                                            <th className="px-6 py-4 text-left font-semibold">Статус</th>
                                            <th className="px-6 py-4 text-left font-semibold">Детали ошибки / Путь к отчету</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 text-sm">
                                        {logs.map((item) => {
                                            const attemptKey = `attempt-${item.id}`;

                                            return (
                                                <tr key={item.id} className="hover:bg-gray-50/50 transition-colors">
                                                    <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                                                        {formatDateTimeMsk(item.created_at)}
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="font-semibold text-gray-900">{item.student_full_name || item.student_login || "—"}</div>
                                                        {/* Копирование ID попытки (только компактная иконка) */}
                                                        <div className="flex items-center gap-1.5 text-xs text-gray-400 mt-1.5 w-fit">
                                                            <span>ID: {item.sdo_attempt_id}</span>
                                                            <button
                                                                onClick={() => handleCopy(item.sdo_attempt_id, 'ID попытки скопирован')}
                                                                className="text-gray-400 hover:text-blue-600 transition-colors"
                                                                title="Копировать ID"
                                                            >
                                                                <ContentCopyIcon style={{ fontSize: 12 }} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 max-w-xs text-gray-700">
                                                        {/* Название программы (кнопка копирования убрана) */}
                                                        <span className="truncate block" title={item.course_name || ""}>
                                                            {item.course_name || "—"}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        {renderStatusAsText(item.status)}
                                                    </td>
                                                    <td className="px-6 py-4 text-xs max-w-sm">
                                                        {/* Облегченный вывод пути без рамки (кнопка копирования убрана) */}
                                                        {item.status.toLowerCase() === 'success' && item.report_path ? (
                                                            <span className="break-all text-gray-700 leading-relaxed">
                                                                {item.report_path}
                                                            </span>
                                                        ) : item.error_message ? (
                                                            <div className="space-y-0.5">
                                                                {item.error_stage && (
                                                                    <div className="font-semibold text-gray-700">{item.error_stage}</div>
                                                                )}
                                                                <div className={`line-clamp-2 leading-relaxed ${
                                                                    item.status.toLowerCase() === 'failed' ? 'text-red-600 font-medium' : 'text-gray-500'
                                                                }`}>
                                                                    {item.error_message}
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <span className="text-gray-400">—</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {!loadingLogs && logsTotal > logsSize && (
                            <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-between bg-gray-50">
                                <div className="text-xs text-gray-500 font-medium">
                                    Показано с {(logsPage - 1) * logsSize + 1} по {Math.min(logsPage * logsSize, logsTotal)} из {logsTotal} записей
                                </div>
                                <AppPagination
                                    page={logsPage}
                                    totalItems={logsTotal}
                                    pageSize={logsSize}
                                    onChange={(p) => setLogsPage(p)}
                                />
                            </div>
                        )}
                    </div>
                </div>
            )
        }
    ];

    return (
        <div className="w-full">
            <div className="p-4 lg:p-8 space-y-6">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-800 leading-tight">
                        Параметры системы
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Конфигурация интеграционных связей, почтового SMTP шлюза и мониторинг логов импорта
                    </p>
                </div>

                <Divider />

                {loadingSettings ? (
                    <div className="flex justify-center items-center h-48">
                        <CircularProgress size={36} />
                    </div>
                ) : (
                    <Tabs
                        items={tabItems}
                        value={activeTab}
                        onChange={(tabId) => setActiveTab(tabId)}
                    />
                )}
            </div>
        </div>
    );
};

export default SettingsPage;